/**
 * Patient-authored completion records for Today actions.
 *
 * The appointments API currently has no attendance/completion field. Keep the
 * event in the patient's notes stream instead, with the source record ID in a
 * structured marker so Home and Care can resolve the same occurrence without
 * relying on local UI state.
 */

export const TODAY_ACTION_COMPLETION_MARKER = "__today_action_completion__";
export const TODAY_ACTION_COMPLETION_VERSION = 1 as const;

export type TodayActionKind = "appointment" | "monitoring" | "task";
/**
 * What she said about the action.
 *
 * `did_not_attend` is a monitoring visit she answered No to. Before it existed
 * that No was saved as `needs_help`, so older records keep that status and are
 * still read as they were written; nothing rewrites them.
 */
export type TodayActionCompletionStatus =
  "completed" | "attended" | "did_not_attend" | "rescheduled" | "needs_help";

export type TodayActionCompletion = {
  [TODAY_ACTION_COMPLETION_MARKER]: true;
  version: typeof TODAY_ACTION_COMPLETION_VERSION;
  actionId: string;
  kind: TodayActionKind;
  status: TodayActionCompletionStatus;
  date: string;
  notes: string;
  recordedAt: string;
  source: "home" | "care";
};

type NoteLike = { text: string };

export type TodayActionCompletionRecord<T extends NoteLike = NoteLike> = {
  note: T;
  completion: TodayActionCompletion;
};

const STATUSES: readonly TodayActionCompletionStatus[] = [
  "completed",
  "attended",
  "did_not_attend",
  "rescheduled",
  "needs_help",
];

const KINDS: readonly TodayActionKind[] = ["appointment", "monitoring", "task"];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isValidDateString(value: string): boolean {
  return !Number.isNaN(Date.parse(value));
}

export function parseTodayActionCompletion(
  text: string | null | undefined,
): TodayActionCompletion | null {
  if (!text) return null;

  try {
    const value: unknown = JSON.parse(text);
    if (!isRecord(value)) return null;
    if (value[TODAY_ACTION_COMPLETION_MARKER] !== true) return null;
    if (value.version !== TODAY_ACTION_COMPLETION_VERSION) return null;
    if (typeof value.actionId !== "string" || value.actionId.length === 0) {
      return null;
    }
    if (!KINDS.includes(value.kind as TodayActionKind)) return null;
    if (!STATUSES.includes(value.status as TodayActionCompletionStatus)) {
      return null;
    }
    if (
      typeof value.date !== "string" ||
      typeof value.recordedAt !== "string"
    ) {
      return null;
    }
    if (
      !isValidDateString(value.date) ||
      !isValidDateString(value.recordedAt)
    ) {
      return null;
    }

    return {
      [TODAY_ACTION_COMPLETION_MARKER]: true,
      version: TODAY_ACTION_COMPLETION_VERSION,
      actionId: value.actionId,
      kind: value.kind as TodayActionKind,
      status: value.status as TodayActionCompletionStatus,
      date: value.date,
      notes: typeof value.notes === "string" ? value.notes : "",
      recordedAt: value.recordedAt,
      source: value.source === "care" ? "care" : "home",
    };
  } catch {
    return null;
  }
}

export function getTodayActionCompletion(
  notes: readonly NoteLike[] | undefined,
  actionId: string,
): TodayActionCompletion | null {
  return getTodayActionCompletionRecord(notes, actionId)?.completion ?? null;
}

export function getTodayActionCompletionRecord<T extends NoteLike>(
  notes: readonly T[] | undefined,
  actionId: string,
): TodayActionCompletionRecord<T> | null {
  if (!notes || actionId.length === 0) return null;

  return notes.reduce<TodayActionCompletionRecord<T> | null>((latest, note) => {
    const parsed = parseTodayActionCompletion(note.text);
    if (!parsed || parsed.actionId !== actionId) return latest;
    if (!latest || parsed.recordedAt > latest.completion.recordedAt) {
      return { note, completion: parsed };
    }
    return latest;
  }, null);
}

export function serializeTodayActionCompletion(
  completion: Omit<
    TodayActionCompletion,
    typeof TODAY_ACTION_COMPLETION_MARKER | "version"
  >,
): string {
  return JSON.stringify({
    [TODAY_ACTION_COMPLETION_MARKER]: true,
    version: TODAY_ACTION_COMPLETION_VERSION,
    ...completion,
  } satisfies TodayActionCompletion);
}
